import { createContext, useState, useEffect } from "react";
import kiLogo from "./../assets/Logo_KI.png";
import scgLogo from "./../assets/Logo_SCG.png";
import {
  MILLISECONDS_PER_DAY,
  FILTERS_SURFACE_ARRAY,
  FILTERS_CLASSIFICATION_ARRAY,
  FILTERS_CREW_ARRAY,
  FILTERS_CREW_SURFACE_ARRAY,
  MONGODB_ATTRIBUTES_DAYS_PAST,
} from "../utility/keys";

// - - - - - - - - - - - - - - - - - - - -

export const defaultUImode = "dark";
export const defaultCompanyList = ["KI"];
export const defaultCompanyCode = "KI";
export const defaultLocationFilter = "CHS";
export const defaultStartDate = "1/6/2025";
export const defaultTab = "scheduler";
export const defaultRoleList = ["coord_all"];
export const defaultJobTitleConfig = [
  { name: "Flags", field: "flags", selected: true },
  { name: "Customer Last Name", field: "lastName", selected: true },
  { name: "Qty", field: "qty", selected: true },
  { name: "Store #", field: "storeNo", selected: true },
  { name: "Arrival", field: "encodedArrival", selected: true },
  { name: "Classification", field: "classification", selected: true },
  { name: "Status", field: "statusCode", selected: false },
  { name: "Invoice #", field: "invoiceNo", selected: false },
  { name: "Project #", field: "projectNo", selected: false },
];
export const defaultFilters = {
  surfaceFilters: [true, true, true, true, true, true, true],
  classificationFilters: [false, true, true, true, true, false, false], // Detail & PaidInError & Other = false
  crewFilters: [false, true, true, true], // Detailer = false
  crewSurfaceFilters: [true, true],
};
export const defaultScreenAccessList = ["optimizer", "scheduler", "jobList"];
export const companies =
  process.env.REACT_APP_ENV !== "production"
    ? [
        { code: "KI", name: "Keller Interiors", logo: kiLogo },
        { code: "KM", name: "Keller Millwork", logo: kiLogo },
        { code: "SCG", name: "SCG", logo: scgLogo },
      ]
    : [
        { code: "KI", name: "Keller Interiors", logo: kiLogo },
        { code: "SCG", name: "SCG", logo: scgLogo },
      ];

// - - - - - - - - - - - - - - - - - - - -

export const AppContext = createContext();

// - - - - - - - - - -

export const AppContextProvider = ({ children }) => {
  // date calculations
  const dateToday = new Date();
  const dateTodayTime = dateToday.getTime();
  const dateTodayDayOfWeek = dateToday.getDay(); // sun=0, mon=1, tue=2
  const dateTodayDifferenceToMonday =
    (1 - dateTodayDayOfWeek) * MILLISECONDS_PER_DAY; // sun=1, mon=0, tue=-1
  const dateMonday = new Date(dateTodayTime + dateTodayDifferenceToMonday);

  // URL parameters
  const [parameterLocation, setParameterLocation] = useState("");
  const [parameterWeek, setParameterWeek] = useState("");

  const [companyCode, setCompanyCode] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [screenAccess, setScreenAccess] = useState("");
  const [locationArray, setLocationArray] = useState([]);
  const [projectDetail, setProjectDetail] = useState([]);
  const [candidateDetail, setCandidateDetail] = useState({});
  const [weekDatesArray, setWeekDatesArray] = useState([]);
  const [mondayThisWeek, setMondayThisWeek] = useState(dateMonday);
  const [latestChangeTS, setLatestChangeTS] = useState(dateToday);
  const [countsToggle, setCountsToggle] = useState(false);
  const [lockAccess_unassigned, setLockAccess_unassigned] = useState(false);
  const [lockAccess_assigned, setLockAccess_assigned] = useState(false);
  const [openJobsDetailModal, setOpenJobsDetailModal] = useState(false);
  // #124 react-joyride tutorial — reset-confirm modal state, lifted
  // here so the Header avatar menu can open it without prop-drilling
  // through to the TutorialOrchestrator that owns the rest of the
  // tutorial flow.
  const [openResetTutorialConfirm, setOpenResetTutorialConfirm] = useState(false);
  const [openCrewDetailModal, setOpenCrewDetailModal] = useState(false);
  const [openUnassignedDetailModal, setOpenUnassignedDetailModal] =
    useState(false);
  const [openExtraCellAddModal, setOpenExtraCellAddModal] = useState(false);
  const [openExtraCellDetailModal, setOpenExtraCellDetailModal] =
    useState(false);
  const [openJobSettingsModal, setOpenJobSettingsModal] = useState(false);
  const [openSuppressModal, setOpenSuppressModal] = useState(false);
  const [openJobInitModal, setOpenJobInitModal] = useState(false);
  // #154 — Repair Order pre-assigned-installer confirmation modal. Opened
  // by schedule.context's useEffect[dropObject] when a Repair Order with a
  // non-empty installer payroll# is dragged onto a cell, unassigned-row,
  // or RTS. Object shape: { show, foundDocument, dropType, dropTargetInstaller }.
  // See `isRepairOrderWithInstaller.js` for the predicate and
  // `RepairOrderConfirmModal.js` for the choices/payload builder.
  const [openRepairOrderConfirmModal, setOpenRepairOrderConfirmModal] = useState(false);
  // #166 — WO Workorder pre-assigned-installer confirmation modal.
  // Sibling of openRepairOrderConfirmModal. Set by schedule.context's
  // useEffect[dropObject] when a record whose `lowes_projectNo` starts
  // with "WO" (case-insensitive) AND has a non-empty installer payroll#
  // is dragged onto a cell, unassigned-row, or RTS. Same object shape.
  // See `isWoWorkorderWithInstaller.js` for the predicate and
  // `WoWorkorderConfirmModal.js` for the choices/payload builder.
  const [openWoWorkorderConfirmModal, setOpenWoWorkorderConfirmModal] = useState(false);
  const [openLocationMetaModal, setOpenLocationMetaModal] = useState(false);
  const [openCrewMetaModal, setOpenCrewMetaModal] = useState(false);
  const [openUserDetailModal, setOpenUserDetailModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  // #139 — Next Day Openings Dashboard modal. Same { show: boolean } object
  // pattern as other modals. Gated on the new `Users.dashboardAccess` flag
  // (admin-flipped in Compass — see canAccessDashboard). Opens via the new
  // circle button alongside Help (?) and Time Machine (🕒) in the header.
  const [openDashboardModal, setOpenDashboardModal] = useState(false);
  // Time Machine #114 Phase 6 — attribute-cell history modal. Opened when
  // the user clicks an attribute pseudo-job in time-travel; shows every
  // EventLog entry whose Attributes doc has matching (_kf_payrollNo, date).
  // Object pattern matches the other modals: { show, payrollNo, date }.
  const [openAttributeHistoryModal, setOpenAttributeHistoryModal] = useState(
    false
  );

  // Time Machine #114 Phase 4.5 — read-only time-travel mode with in-memory
  // event-slider scrub.
  //
  // The user picks a DATE (timeTravelDate). The Lambda
  // (`getStateAndEventsForDate` v05-08-2026-04-QRY01+) returns the baseline
  // state at 00:00 UTC of that date plus every event for that date. ModelTsContext
  // caches both for each of 3 collections.
  //
  // The user then scrubs a time SLIDER on that date. timeTravelSliderTime
  // tracks the slider position. ModelTsContext computes the displayed state
  // by applying events whose timestamp <= timeTravelSliderTime to the baseline
  // (in-memory replay — zero new API calls per slider position).
  //
  // timeTravelTimestamp is exposed as a derived alias of timeTravelSliderTime
  // for components that don't care about the date-vs-slider distinction
  // (Banner, ReadOnlyShield, TimeMachineJobModal/CrewModal in Phase 5).
  //
  // enterTimeTravel(date): sets timeTravelDate and seeds timeTravelSliderTime
  // to end-of-day UTC of that date. The TimeSlider component snaps the slider
  // visually to the time-of-last-event tick when queries return.
  const [isTimeTraveling, setIsTimeTraveling] = useState(false);
  const [timeTravelDate, setTimeTravelDate] = useState(null);
  const [timeTravelSliderTime, setTimeTravelSliderTime] = useState(null);
  // Backward-compat alias — components reading timeTravelTimestamp see the
  // current slider position. New components should prefer timeTravelSliderTime
  // for clarity but both names are equivalent.
  const timeTravelTimestamp = timeTravelSliderTime;

  // Time Machine #114 Phase 6 — Diff Pair view (Optimizer tab only).
  //
  // When isDiffPairView is true, the Optimizer renders a split-screen with
  // two grids: the left tied to timeTravelSliderTime (T1) and the right tied
  // to diffPairRightSliderTime (T2 ≥ T1).
  //
  // Default seeding (Phase 7 review feedback, 2026-05-11): T1 seeds to
  // 00:00 local and T2 to 23:59 local so the user sees the whole day's
  // diff at a glance on entry — that's the view most schedulers were
  // manually building on every entry anyway. Both sliders are user-
  // draggable from there; the previous "auto-snap T2 to next event
  // after T1" behavior was removed because it fought the whole-day
  // default. Day-navigation re-seeds both sliders to the new day's
  // 00:00 / 23:59 boundaries.
  const [isDiffPairView, setIsDiffPairView] = useState(false);
  const [diffPairRightSliderTime, setDiffPairRightSliderTime] = useState(null);
  // Phase 6 mute-highlights toggle (banner checkbox). When true, JobTile's
  // diff outline opacity drops and UserBubbleStack opacity drops to 0.35
  // so the user can investigate something else without diff visuals
  // competing for attention. Off by default; reset on time-travel exit.
  const [isDiffHighlightsMuted, setIsDiffHighlightsMuted] = useState(false);

  // Computed flag: true if ANY modal is open - used to pause polling during editing
  // This prevents race conditions where polling overwrites optimistic UI updates
  // All modals use { show: boolean } object pattern, so check .show property
  const isAnyModalOpen =
    openJobsDetailModal?.show ||
    openCrewDetailModal?.show ||
    openUnassignedDetailModal?.show ||
    openExtraCellAddModal?.show ||
    openExtraCellDetailModal?.show ||
    openJobSettingsModal?.show ||
    openSuppressModal?.show ||
    openJobInitModal?.show ||
    openRepairOrderConfirmModal?.show ||
    openWoWorkorderConfirmModal?.show ||
    openLocationMetaModal?.show ||
    openCrewMetaModal?.show ||
    openUserDetailModal?.show ||
    openHelpModal?.show ||
    openDashboardModal?.show ||
    openAttributeHistoryModal?.show ||
    isTimeTraveling;

  const surfaceFilters = new Array(FILTERS_SURFACE_ARRAY.length).fill(true);
  const classificationFilters = new Array(
    FILTERS_CLASSIFICATION_ARRAY.length
  ).fill(true);
  const crewFilters = new Array(FILTERS_CREW_ARRAY.length).fill(true);
  const crewSurfaceFilters = new Array(FILTERS_CREW_SURFACE_ARRAY.length).fill(
    true
  );
  const [currentFilters, setCurrentFilters] = useState({
    surfaceFilters: surfaceFilters,
    classificationFilters: classificationFilters,
    crewFilters: crewFilters,
    crewSurfaceFilters: crewSurfaceFilters,
  });
  const [darkMode, setDarkMode] = useState(
    JSON.parse(localStorage.getItem("darkMode")) ?? true
  );
  const [whichList, setWhichListInternal] = useState("schedule");
  const [tabSwitching, setTabSwitching] = useState(false);
  const [largeView, setLargeView] = useState(
    JSON.parse(localStorage.getItem("largeView")) ?? true
  );
  const [jobTitleConfig, setJobTitleConfig] = useState("");
  // JSON.parse(localStorage.getItem("jobTitleConfig")) ?? JOB_INFO_CHECKBOXES

  const environs = {
    nodeEnv: process.env.NODE_ENV,
    appName: process.env.REACT_APP_NAME,
    appEnv: process.env.REACT_APP_ENV,
    mongoDb: process.env.REACT_APP_MONGODB_DATABASE,
    hostName: window.location.hostname,
  };
  console.log(
    "%cAppContext  hostname: ",
    "color: #FFA500; font-weight: bold;",
    environs.hostName,
    ", NODE_ENV: ",
    environs.nodeEnv,
    ", REACT_APP_NAME: ",
    environs.appName,
    ", REACT_APP_ENV: ",
    environs.appEnv,
    ", MONGODB_DATABASE: ",
    environs.mongoDb
  );
  const [appEnvirons, setAppEnvirons] = useState(environs);
  const [defaultNewUserRecord, setDefaultNewUserRecord] = useState({
    companyList: defaultCompanyList,
    defaultCompany: defaultCompanyCode,
    defaultLocation: defaultLocationFilter,
    defaultStartDate: defaultStartDate,
    defaultDate: defaultStartDate,
    defaultTab: defaultTab,
    defaultUImode: defaultUImode,
    defaultCountsToggle: false,
    defaultFilters: defaultFilters,
    locationList: [],
    roleList: defaultRoleList,
    screenAccessList: defaultScreenAccessList,
    jobTitleConfig: defaultJobTitleConfig,
    conversion: "tanstackOnly",
    devTools: true,
  });

  // - - - - - - - - - -

  /**
   * Transforms incoming MongoDB document data into required date format.
   * @param {string} scheduledDate - The scheduled date.
   * @returns {string} - The formatted date string.
   */
  const dataFormatting_dateFields = (scheduledDate) => {
    // transform incoming MongoDB document data into required format
    // remove leading zeros from scheduledDate
    return new Date(scheduledDate).toLocaleDateString("en-us");
  };

  // - - - - - - - - - -

  /**
   * Transforms incoming MongoDB document data into required payroll format.
   * @param {string} payrollNo - The payroll number.
   * @returns {string} - The formatted payroll number.
   */
  const dataFormatting_payrollFields = (payrollNo) => {
    // transform incoming MongoDB document data into required format
    const payrollString = payrollNo.toString();
    const payrollLength = payrollString.length;
    const decimalPosition = payrollString.indexOf(".");

    if (decimalPosition === payrollLength - 2) {
      // suffix has a single digit ie 3264.4
      payrollNo = payrollNo.toString() + "0";
    }

    // pad leading zeros to front of payrollNos
    return payrollNo.toString().padStart(7, "0");
  };

  // - - - - - - - - - -

  /**
   * Transforms incoming MongoDB document data into U.I. small title.
   * @param {Object} job - The job object.
   * @returns {string} - The encoded small title.
   */
  const dataFormatting_encodedSmallTitle = (job) => {
    // transform incoming MongoDB document data into U.I. title
    // KellerInteriors::Kanban = "slots":["Qty","Surface","StoreNo","Arrival","Name_Last"]
    const expressShip = job.expressShip === 1 ? "📦" : "";
    const furnitureFlag = job.furniture ? "🪑" : "";
    const prescheduledFlag = job.prescheduled ? "📅" : "";
    const rushFlag = job.rush ? "⚡️" : "";
    const expedite = job.jobAttributes?.expedite ? "🏃🏽‍♂️" : "";
    const distant = job.jobAttributes?.distant ? "✈️" : "";
    const imsWarning = job.imsWarning ? "🛑" : "";
    var encodedSurface = "";
    switch (job.surface) {
      case "Carpet":
        encodedSurface = "C";
        break;
      case "Vinyl":
        encodedSurface = "V";
        break;
      case "Plank":
        encodedSurface = "P";
        break;
      case "Ceramic":
        encodedSurface = "T";
        break;
      case "Wood":
        encodedSurface = "W";
        break;
      case "Backsplash":
        encodedSurface = "B";
        break;
      case "Pad":
        break;
      case "Internal":
        encodedSurface = "ID";
        break;
      case "External":
        encodedSurface = "ED";
        break;
      case "Storm":
        encodedSurface = "SD";
        break;
      case "Window":
        encodedSurface = "WD";
        break;
      default:
        encodedSurface = "U";
    }

    var encodedArrival = "";
    job.arrival === ""
      ? (encodedArrival = "?-?")
      : (encodedArrival = job.arrival);
    encodedArrival = encodedArrival.replaceAll(" ", "");
    encodedArrival = encodedArrival.replaceAll(":00AM", "");
    encodedArrival = encodedArrival.replaceAll(":00PM", "");
    encodedArrival = encodedArrival.replaceAll(":30AM", ":30");
    encodedArrival = encodedArrival.replaceAll(":30PM", ":30");
    encodedArrival = encodedArrival.replaceAll("a.m.", "");
    encodedArrival = encodedArrival.replaceAll("p.m.", "");
    encodedArrival = encodedArrival.replaceAll("Noon", "12");
    encodedArrival = encodedArrival.replaceAll("Morning", "");
    encodedArrival = encodedArrival.replaceAll("Afternoon", "");
    encodedArrival = encodedArrival.replaceAll("Evening", "");

    var encodedSmallTitle = "";
    encodedSmallTitle += rushFlag;
    encodedSmallTitle += expressShip;
    encodedSmallTitle += expedite;
    encodedSmallTitle += distant;
    encodedSmallTitle += imsWarning;
    encodedSmallTitle += job.qty;
    encodedSmallTitle += encodedSurface;
    encodedSmallTitle += job.storeNo;
    encodedSmallTitle += furnitureFlag;
    encodedSmallTitle += prescheduledFlag;
    encodedSmallTitle += encodedArrival;
    encodedSmallTitle += job.lastName;

    return encodedSmallTitle;
  };

  // - - - - - - - - - -

  /**
   * Transforms incoming MongoDB document data into U.I. large title.
   * @param {Object} job - The job object.
   * @returns {string} - The encoded large title.
   */
  const dataFormatting_encodedLargeTitle = (job) => {
    // transform incoming MongoDB document data into U.I. title
    // KellerInteriors::Kanban = "slots":["Qty","Surface","StoreNo","Arrival","Name_Last"]
    const expressShip = job.expressShip === 1 ? "📦" : "";
    const furnitureFlag = job.furniture ? "🪑" : "";
    const prescheduledFlag = job.prescheduled ? "📅" : "";
    const rushFlag = job.rush ? "⚡️" : "";
    const expedite = job.jobAttributes?.expedite ? "🏃🏽‍♂️" : "";
    const distant = job.jobAttributes?.distant ? "✈️" : "";
    const imsWarning = job.imsWarning ? "🛑" : "";
    var encodedSurface = "";
    switch (job.surface) {
      case "Carpet":
        encodedSurface = "Cpt";
        break;
      case "Vinyl":
        encodedSurface = "Vyl";
        break;
      case "Plank":
        encodedSurface = "Plk";
        break;
      case "Ceramic":
        encodedSurface = "Til";
        break;
      case "Wood":
        encodedSurface = "Wd";
        break;
      case "Backsplash":
        encodedSurface = "Bksp";
        break;
      case "Pad":
        break;
      case "Internal":
        encodedSurface = "InDr";
        break;
      case "External":
        encodedSurface = "ExDr";
        break;
      case "Storm":
        encodedSurface = "StDr";
        break;
      case "Window":
        encodedSurface = "Win";
        break;
      default:
        encodedSurface = "UNK";
    }

    var encodedArrival = "";
    job.arrival === ""
      ? (encodedArrival = "?-?")
      : (encodedArrival = job.arrival);
    encodedArrival = encodedArrival.replaceAll(" ", "");
    encodedArrival = encodedArrival.replaceAll(":00AM", "");
    encodedArrival = encodedArrival.replaceAll(":00PM", "");
    encodedArrival = encodedArrival.replaceAll(":30AM", ":30");
    encodedArrival = encodedArrival.replaceAll(":30PM", ":30");
    encodedArrival = encodedArrival.replaceAll("a.m.", "");
    encodedArrival = encodedArrival.replaceAll("p.m.", "");
    encodedArrival = encodedArrival.replaceAll("Noon", "12");
    encodedArrival = encodedArrival.replaceAll("Morning", "");
    encodedArrival = encodedArrival.replaceAll("Afternoon", "");
    encodedArrival = encodedArrival.replaceAll("Evening", "");

    var encodedLargeTitle = "";
    encodedLargeTitle += rushFlag;
    encodedLargeTitle += rushFlag ? "•" : "";
    encodedLargeTitle += expressShip;
    encodedLargeTitle += expressShip ? "•" : "";
    encodedLargeTitle += expedite;
    encodedLargeTitle += expedite ? "•" : "";
    encodedLargeTitle += distant;
    encodedLargeTitle += distant ? "•" : "";
    encodedLargeTitle += imsWarning;
    encodedLargeTitle += imsWarning ? "•" : "";
    encodedLargeTitle += job.qty;
    encodedLargeTitle += "•";
    encodedLargeTitle += encodedSurface;
    encodedLargeTitle += "•";
    encodedLargeTitle += job.storeNo;
    encodedLargeTitle += "•";
    encodedLargeTitle += furnitureFlag;
    encodedLargeTitle += furnitureFlag ? "•" : "";
    encodedLargeTitle += prescheduledFlag;
    encodedLargeTitle += prescheduledFlag ? "•" : "";
    encodedLargeTitle += encodedArrival;
    encodedLargeTitle += "•";
    encodedLargeTitle += job.lastName;

    return encodedLargeTitle;
  };

  // - - - - - - - - - -

  /**
   * Transforms job data into a new encoded small title based on job title configuration.
   * @param {Object} job - The job object.
   * @param {Array} jobTitleConfig - The job title configuration array.
   * @returns {string} - The encoded small title.
   */
  const dataFormatting_encodedSmallTitleNew = (job, jobTitleConfig) => {
    const encodedSmallTitle = jobTitleConfig.reduce((title, conf) => {
      if (job[conf.field] && conf.selected) {
        title += title ? ` • ${job[conf.field]}` : job[conf.field];
      }
      return title;
    }, "");

    return encodedSmallTitle;
  };

  // - - - - - - - - - -

  /**
   * Transforms job data into a new encoded large title based on job title configuration.
   * @param {Object} job - The job object.
   * @param {Array} jobTitleConfig - The job title configuration array.
   * @returns {string} - The encoded large title.
   */
  const dataFormatting_encodedLargeTitleNew = (job, jobTitleConfig) => {
    const encodedLargeTitle = jobTitleConfig.reduce((title, conf) => {
      if (job[conf.field] && conf.selected) {
        title += title ? `\n${job[conf.field]}` : job[conf.field];
      }
      return title;
    }, "");

    return encodedLargeTitle;
  };

  // - - - - - - - - - -

  /**
   * Creates a UUID.
   * @returns {string} - The generated UUID.
   */
  const createUuid = () => {
    // https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  };

  // - - - - - - - - - -

  /**
   * Creates an array of dates for the week starting from the given start date.
   * @param {Date} startDate - The start date.
   */
  const createWeekDatesArray = (startDate) => {
    // user is changing startDate - update the state
    const newArray = [
      // startDate.toLocaleDateString("en-us"),
      new Date(Date.parse(startDate)).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 1
      ).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 2
      ).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 3
      ).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 4
      ).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 5
      ).toLocaleDateString("en-us"),
      new Date(
        Date.parse(startDate) + MILLISECONDS_PER_DAY * 6
      ).toLocaleDateString("en-us"),
    ];
    setWeekDatesArray(newArray);
    console.log("%cAppContext createWeekDatesArray: ", "color: #FFA500; font-weight: bold;", newArray);
  };

  // - - - - - - - - - -

  /**
   * Sets the week to the current week.
   */
  const onWeekToday = () => {
    // date calculations
    const dateToday = new Date();
    const dateTodayTime = dateToday.getTime();
    const dateTodayDayOfWeek = dateToday.getDay(); // sun=0, mon=1, tue=2
    const dateTodayDifferenceToMonday =
      (1 - dateTodayDayOfWeek) * MILLISECONDS_PER_DAY; // sun=1, mon=0, tue=-1
    const dateMonday = new Date(dateTodayTime + dateTodayDifferenceToMonday);

    if (weekDatesArray.length === 7) {
      // user is changing weeks - update the View
      setWeekDatesArray([]);
      console.log("%cAppContext onWeekToday...", "color: #FFA500; font-weight: bold;");
      setMondayThisWeek(dateMonday);
      const newDate = new Date(dateMonday);
      createWeekDatesArray(newDate);
    } else {
      console.log("%cAppContext onWeekToday SKIPPING (weekDayArray != 7)...", "color: #FFA500; font-weight: bold;");
    }
  };

  // - - - - - - - - - -

  // 2025-01-29 - adding an extra 12 hours (43200000) to this formula so
  //              that adding or removing days from a Date value (which
  //              uses 00:00:00 as the time) will not bugger up around
  //              dates that utilize a time change, which causes the calc
  //              to use 23:00:00 as a result of the math, which incorrectly
  //              results in a day before
  //

  /**
   * Checks if navigating to a week would exceed the historical data limit.
   * @param {Date} targetMondayDate - The Monday date of the target week.
   * @returns {boolean} - True if navigation would exceed limit.
   */
  const isWeekBeyondLimit = (targetMondayDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate the cutoff date (X days in the past from today)
    const cutoffDate = new Date(
      today.getTime() - MONGODB_ATTRIBUTES_DAYS_PAST * MILLISECONDS_PER_DAY
    );
    cutoffDate.setHours(0, 0, 0, 0);

    // Check if the target Monday is before the cutoff
    return targetMondayDate < cutoffDate;
  };

  /**
   * Checks if navigating one week back from current view would exceed the limit.
   * Used by UI to disable the back navigation button.
   * @returns {boolean} - True if back navigation is allowed.
   */
  const canNavigateWeekBack = () => {
    if (weekDatesArray.length !== 7) return false;

    const targetDate = new Date(
      Date.parse(weekDatesArray[0]) - MILLISECONDS_PER_DAY * 7 + 43200000
    );

    return !isWeekBeyondLimit(targetDate);
  };

  /**
   * Sets the week to the previous week.
   */
  const onWeekBehind = () => {
    if (weekDatesArray.length === 7) {
      const newDate = new Date(
        Date.parse(weekDatesArray[0]) - MILLISECONDS_PER_DAY * 7 + 43200000
      );

      // Check if navigating back would exceed the historical data limit
      if (isWeekBeyondLimit(newDate)) {
        console.log(
          "%cAppContext onWeekBehind BLOCKED - exceeds historical limit",
          "color: #FF0000; font-weight: bold;",
          `Target: ${newDate.toLocaleDateString("en-us")}, Limit: ${MONGODB_ATTRIBUTES_DAYS_PAST} days`
        );
        return; // Don't navigate
      }

      // user is changing weeks - update the View
      setWeekDatesArray([]);
      console.log("%cAppContext onWeekBehind...", "color: #FFA500; font-weight: bold;");
      createWeekDatesArray(newDate);
    } else {
      console.log("%cAppContext onWeekToday SKIPPING (weekDayArray != 7)...", "color: #FFA500; font-weight: bold;");
    }
  };

  // - - - - - - - - - -

  /**
   * Sets the week to the next week.
   */
  const onWeekAhead = () => {
    if (weekDatesArray.length === 7) {
      // user is changing weeks - update the View
      setWeekDatesArray([]);
      console.log("%cAppContext onWeekAhead...", "color: #FFA500; font-weight: bold;");
      const newDate = new Date(
        Date.parse(weekDatesArray[0]) + MILLISECONDS_PER_DAY * 7 + 43200000
      );
      createWeekDatesArray(newDate);
    } else {
      console.log("%cAppContext onWeekToday SKIPPING (weekDayArray != 7)...", "color: #FFA500; font-weight: bold;");
    }
  };

  // - - - - - - - - - -

  /**
   * Handles changes to surface filters.
   * @param {number} position - The position of the filter to change.
   */
  const onSurfaceFiltersChanged = (position) => {
    // user is changing filters - update the state
    //
    // ----- TEMPORARILY DISABLING FILTER CHANGING -----
    //
    console.log(
      "%cAppContext onSurfaceFiltersChanged[",
      "color: #FFA500; font-weight: bold;",
      position,
      "]...----- TEMPORARILY DISABLING"
    );
    const updatedCheckedState = currentFilters.surfaceFilters.map(
      (item, index) => (index === position ? !item : item)
    );
    var newFilters = { ...currentFilters };
    newFilters.surfaceFilters = updatedCheckedState;
    console.log(
      "%cAppContext onSurfaceFiltersChanged newFilters: ",
      "color: #FFA500; font-weight: bold;",
      newFilters,
      "----- TEMPORARILY DISABLING"
    );
    // setCurrentFilters(newFilters);
  };

  // - - - - - - - - - -

  /**
   * Handles changes to classification filters.
   * @param {number} position - The position of the filter to change.
   */
  const onClassificationFiltersChanged = (position) => {
    // user is changing filters - update the state
    //
    // ----- TEMPORARILY DISABLING FILTER CHANGING -----
    //
    console.log(
      "%cAppContext onClassificationFiltersChanged[",
      "color: #FFA500; font-weight: bold;",
      position,
      "]...----- TEMPORARILY DISABLING"
    );
    const updatedCheckedState = currentFilters.classificationFilters.map(
      (item, index) => (index === position ? !item : item)
    );
    var newFilters = { ...currentFilters };
    newFilters.classificationFilters = updatedCheckedState;
    // setCurrentFilters(newFilters);
  };

  // - - - - - - - - - -

  /**
   * Handles changes to crew filters.
   * @param {number} position - The position of the filter to change.
   */
  const onCrewFiltersChanged = (position) => {
    // user is changing filters - update the state
    //
    // ----- TEMPORARILY DISABLING FILTER CHANGING -----
    //
    console.log(
      "%cAppContext onCrewFiltersChanged[",
      "color: #FFA500; font-weight: bold;",
      position,
      "]...----- TEMPORARILY DISABLING"
    );
    const updatedCheckedState = currentFilters.crewFilters.map((item, index) =>
      index === position ? !item : item
    );
    var newFilters = { ...currentFilters };
    newFilters.crewFilters = updatedCheckedState;
    // setCurrentFilters(newFilters);
  };

  // - - - - - - - - - -

  /**
   * Handles changes to crew surface filters.
   * @param {number} position - The position of the filter to change.
   */
  const onCrewSurfaceFiltersChanged = (position) => {
    // user is changing filters - update the state
    //
    // ----- TEMPORARILY DISABLING FILTER CHANGING -----
    //
    console.log(
      "%cAppContext onCrewSurfaceFiltersChanged[",
      "color: #FFA500; font-weight: bold;",
      position,
      "]...----- TEMPORARILY DISABLING"
    );
    const updatedCheckedState = currentFilters.crewSurfaceFilters.map(
      (item, index) => (index === position ? !item : item)
    );
    var newFilters = { ...currentFilters };
    newFilters.crewSurfaceFilters = updatedCheckedState;
    // setCurrentFilters(newFilters);
  };

  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  //
  // AppContext launch                -> createWeekDatesArray(mondayThisWeek)
  //

  // Wrapper for setWhichList that shows loading indicator during tab switch
  // Uses a delay to allow the CSS spinner animation to start before React blocks the thread
  const setWhichList = (newTab) => {
    console.log(
      "%c🔄 setWhichList called:",
      "color: #00BFFF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      "newTab:", newTab,
      "| current whichList:", whichList,
      "| will update:", newTab !== whichList
    );
    if (newTab !== whichList) {
      setTabSwitching(true);
      // Use requestAnimationFrame to ensure the loader is painted and animating
      // before we start the heavy tab render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double rAF ensures the browser has painted the loader
          console.log(
            "%c🔄 setWhichList - actually setting to:",
            "color: #00FF00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
            newTab
          );
          setWhichListInternal(newTab);
          // Small delay before clearing to ensure smooth transition
          setTimeout(() => {
            setTabSwitching(false);
          }, 100);
        });
      });
    }
  };

  useEffect(() => {
    console.log("%cAppContext useEffect[]...(createWeekDatesArray)", "color: #FFA500; font-weight: bold;");
    // initial app launch
    if (weekDatesArray.length === 0) {
      createWeekDatesArray(mondayThisWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // - - - - - - - - - -

  useEffect(() => {
    console.log(
      "%cAppContext useEffect[mondayThisWeek]...(createWeekDatesArray)",
      "color: #FFA500; font-weight: bold;"
    );
    // when startDate changes
    createWeekDatesArray(mondayThisWeek);
  }, [mondayThisWeek]);

  // - - - - - - - - - - - - - - - - -  TIME MACHINE HELPERS  - - - - - - - - - - - - - - - - - -
  //
  // Time Machine #114 Phase 4 helpers. enterTimeTravel(timestamp) closes any
  // open modals (so users don't carry a stale modal across the live → historical
  // boundary) and flips the toggle. exitTimeTravel() returns to live by clearing
  // both pieces of state. The schedule grid + ModelTsContext queries observe
  // isTimeTraveling and swap their data source automatically.

  const closeAllModals = () => {
    setOpenJobsDetailModal(false);
    setOpenCrewDetailModal(false);
    setOpenUnassignedDetailModal(false);
    setOpenExtraCellAddModal(false);
    setOpenExtraCellDetailModal(false);
    setOpenJobSettingsModal(false);
    setOpenSuppressModal(false);
    setOpenJobInitModal(false);
    setOpenRepairOrderConfirmModal(false);
    setOpenWoWorkorderConfirmModal(false);
    setOpenLocationMetaModal(false);
    setOpenCrewMetaModal(false);
    setOpenUserDetailModal(false);
    setOpenHelpModal(false);
    setOpenAttributeHistoryModal(false);
  };

  /**
   * Enter time-travel mode for a specific date.
   *
   * @param {Date} date  Any Date — only the calendar day is used. The slider
   *                     starts at end-of-day-UTC of that day and is bounded
   *                     to that day. The user scrubs left through events.
   *
   * Phase 4.5 picker (DayPicker) hands a Date in. Slider then refines within
   * the day. enterTimeTravel does NOT take a full timestamp anymore; the
   * caller picks the day, the slider picks the moment within the day.
   */
  const enterTimeTravel = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error(
        "%cAppContext enterTimeTravel: invalid Date arg, ignoring",
        "color: #ff0000; font-weight: bold;",
        date
      );
      return;
    }
    // Use the input's LOCAL calendar components and encode as a LOCAL-midnight
    // Date. Phase 6 dev validation surfaced that a UTC-midnight encoding made
    // the TimeSlider span [UTC 00:00, UTC 23:59], which displays as
    // [yesterday 20:00 EDT, today 19:59 EDT] — wraps around midnight in the
    // user's clock (the "1915 at far right" / "goes backwards too early a.m."
    // bug). Local-midnight encoding produces a 24h window that aligns with
    // the user's wall clock: slider far-left = 00:00 local, far-right =
    // 23:59 local, monotonic in display.
    //
    // The Lambda's getStateAndEventsForDate still receives a YYYY-MM-DD
    // string and fetches the corresponding UTC day window — this matches
    // when local-day and UTC-day labels agree (typical case). Edge
    // forensics (events in the last few hours before midnight) may need
    // a Lambda-side timezone fix in a follow-up; documented for now.
    const yyyy = date.getFullYear();
    const mm = date.getMonth();
    const dd = date.getDate();
    const dayStart = new Date(yyyy, mm, dd, 0, 0, 0, 0);
    const dayEnd = new Date(yyyy, mm, dd, 23, 59, 59, 999);
    console.log(
      "%cAppContext enterTimeTravel:",
      "color: #FFA500; font-weight: bold;",
      `localDate=${yyyy}-${String(mm+1).padStart(2,"0")}-${String(dd).padStart(2,"0")} → localMidnight=${dayStart.toISOString()} sliderInit=${dayEnd.toISOString()}`
    );
    closeAllModals();
    setTimeTravelDate(dayStart);

    // Phase 6 — session-level preferences (isDiffPairView, isDiffHighlightsMuted)
    // are reset to defaults ONLY on FIRST entry. enterTimeTravel is also
    // called on day-nav (DayPicker prev/next/today + TimeMachineBanner
    // adjust path); on those calls, the user's existing toggle states must
    // persist. User-reported bug: launch time-travel → toggle DiffPair off
    // → navigate to prior day → DiffPair re-launched because the
    // unconditional setIsDiffPairView(true) below wiped the toggle. Same
    // would have happened to the Mute Highlights checkbox preference.
    const firstEntry = !isTimeTraveling;
    if (firstEntry) {
      // First entry into time-travel — DiffPair view is ON by default.
      // Users can collapse via the Add-Grid icon or either grid's close (X).
      setIsDiffPairView(true);
      setIsDiffHighlightsMuted(false);
    }

    // Slider seeding diverges by view mode (Phase 7 review feedback):
    //   - Diff Pair: T1 seeds at 00:00, T2 at 23:59 — whole-day view.
    //   - Single grid: T1 seeds at 23:59 — scrub backward into history.
    // diffPairWillBeActive captures BOTH the first-entry case (toggle
    // just turned on above) and the day-nav case (toggle was already on
    // before this enterTimeTravel call).
    const diffPairWillBeActive = firstEntry || isDiffPairView;
    if (diffPairWillBeActive) {
      setTimeTravelSliderTime(dayStart);
      setDiffPairRightSliderTime(dayEnd);
    } else {
      setTimeTravelSliderTime(dayEnd);
      setDiffPairRightSliderTime(null);
    }

    setIsTimeTraveling(true);
  };

  /**
   * Scrub the time-travel state to a specific moment. Updates timeTravelDate
   * (so the bundle re-fetches if the moment is on a different day than the
   * current view) AND timeTravelSliderTime (so the slider position lands on
   * the exact moment within that day).
   *
   * Used by EventHistoryList's double-click handler — works for events on
   * the SAME day as the current view AND for events on a DIFFERENT day.
   * Phase 5 ship'd a same-day-only path (just setTimeTravelSliderTime),
   * which silently failed when the user double-clicked an event from a
   * past day in the History feed (slider value updated but the bundle
   * was wrong day → replay produced baseline-at-bundle-start, the schedule
   * grid appeared not to change). This helper closes that gap without
   * triggering enterTimeTravel's broader side effects (closeAllModals,
   * isDiffPairView reset, isDiffHighlightsMuted reset, isTimeTraveling
   * setter), which would have unintended UX consequences when the user
   * is already in time-travel and just wants to jump to a moment.
   */
  const scrubToTimestamp = (timestamp) => {
    if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
      console.error(
        "%cAppContext scrubToTimestamp: invalid Date arg, ignoring",
        "color: #ff0000; font-weight: bold;",
        timestamp
      );
      return;
    }
    // Build a LOCAL-midnight Date for the event's calendar day. Same
    // convention as enterTimeTravel — local-day boundaries throughout.
    const yyyy = timestamp.getFullYear();
    const mm = timestamp.getMonth();
    const dd = timestamp.getDate();
    const eventDayStart = new Date(yyyy, mm, dd, 0, 0, 0, 0);

    // Only swap the date if it actually differs (avoid a spurious bundle
    // refetch when the moment is on the same day as the current view).
    const sameDay =
      timeTravelDate instanceof Date &&
      timeTravelDate.getFullYear() === yyyy &&
      timeTravelDate.getMonth() === mm &&
      timeTravelDate.getDate() === dd;
    if (!sameDay) {
      console.log(
        "%cAppContext scrubToTimestamp: cross-day scrub",
        "color: #FFA500; font-weight: bold;",
        `from=${
          timeTravelDate instanceof Date ? timeTravelDate.toISOString() : "(null)"
        } to=${eventDayStart.toISOString()} sliderAt=${timestamp.toISOString()}`
      );
      setTimeTravelDate(eventDayStart);
    }
    setTimeTravelSliderTime(timestamp);
  };

  const exitTimeTravel = () => {
    console.log(
      "%cAppContext exitTimeTravel",
      "color: #FFA500; font-weight: bold;"
    );
    setIsTimeTraveling(false);
    setTimeTravelDate(null);
    setTimeTravelSliderTime(null);
    setIsDiffPairView(false);
    setDiffPairRightSliderTime(null);
    setIsDiffHighlightsMuted(false);
  };

  // When the Diff Pair toggle flips off, drop the right slider so a future
  // re-entry into Diff Pair starts fresh (the auto-snap effect in
  // ModelTsContext will populate it from "next event after T1").
  useEffect(() => {
    if (!isDiffPairView && diffPairRightSliderTime != null) {
      setDiffPairRightSliderTime(null);
    }
  }, [isDiffPairView, diffPairRightSliderTime]);

  // - - - - - - - - - - - - - - - - -  CONTEXT RETURN  - - - - - - - - - - - - - - - - - -

  return (
    <AppContext.Provider
      value={{
        companyCode,
        locationFilter,
        jobTitleConfig,
        screenAccess,
        currentFilters,
        locationArray,
        projectDetail,
        candidateDetail,
        mondayThisWeek,
        weekDatesArray,
        latestChangeTS,
        countsToggle,
        lockAccess_unassigned,
        lockAccess_assigned,
        whichList,
        tabSwitching,
        openJobsDetailModal,
        openResetTutorialConfirm,
        setOpenResetTutorialConfirm,
        openCrewDetailModal,
        openUnassignedDetailModal,
        openExtraCellAddModal,
        openExtraCellDetailModal,
        largeView,
        darkMode,
        openJobSettingsModal,
        openSuppressModal,
        openJobInitModal,
        openRepairOrderConfirmModal,
        openWoWorkorderConfirmModal,
        openLocationMetaModal,
        openCrewMetaModal,
        openUserDetailModal,
        openHelpModal,
        openDashboardModal,
        openAttributeHistoryModal,
        setOpenAttributeHistoryModal,
        isAnyModalOpen,
        isTimeTraveling,
        timeTravelDate,
        timeTravelSliderTime,
        setTimeTravelSliderTime,
        timeTravelTimestamp, // backward-compat alias (= timeTravelSliderTime)
        isDiffPairView,
        setIsDiffPairView,
        diffPairRightSliderTime,
        setDiffPairRightSliderTime,
        isDiffHighlightsMuted,
        setIsDiffHighlightsMuted,
        enterTimeTravel,
        scrubToTimestamp,
        exitTimeTravel,
        closeAllModals,
        appEnvirons,
        parameterLocation,
        parameterWeek,
        defaultNewUserRecord,
        setCompanyCode,
        setLocationFilter,
        setJobTitleConfig,
        setScreenAccess,
        setLocationArray,
        setCurrentFilters,
        setWeekDatesArray,
        setMondayThisWeek,
        setProjectDetail,
        setCandidateDetail,
        setLatestChangeTS,
        setCountsToggle,
        setLockAccess_unassigned,
        setLockAccess_assigned,
        createUuid,
        dataFormatting_dateFields,
        dataFormatting_payrollFields,
        dataFormatting_encodedSmallTitle,
        dataFormatting_encodedLargeTitle,
        dataFormatting_encodedSmallTitleNew,
        dataFormatting_encodedLargeTitleNew,
        createWeekDatesArray,
        onWeekToday,
        onWeekBehind,
        onWeekAhead,
        canNavigateWeekBack,
        onSurfaceFiltersChanged,
        onClassificationFiltersChanged,
        onCrewFiltersChanged,
        onCrewSurfaceFiltersChanged,
        setWhichList,
        setOpenJobsDetailModal,
        setOpenCrewDetailModal,
        setOpenUnassignedDetailModal,
        setOpenExtraCellAddModal,
        setOpenExtraCellDetailModal,
        setLargeView,
        setDarkMode,
        setOpenJobSettingsModal,
        setOpenSuppressModal,
        setOpenJobInitModal,
        setOpenRepairOrderConfirmModal,
        setOpenWoWorkorderConfirmModal,
        setOpenLocationMetaModal,
        setOpenCrewMetaModal,
        setOpenUserDetailModal,
        setOpenHelpModal,
        setOpenDashboardModal,
        setAppEnvirons,
        setParameterLocation,
        setParameterWeek,
        setDefaultNewUserRecord,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
